import { CollectionsEmpty } from '@/components/features/Collection/CollectionsGrid/CollectionsEmpty';
import { CollectionCard } from '@/components/shared/CollectionCard';
import { useAuthentication } from '@/hooks/useAuthentication';
import { getSupportedChainById } from '@/utils/getChain';
import { routes } from '@/utils/routes';
import { Container, Flex, SimpleGrid } from '@chakra-ui/react';
import { compareAddresses } from '@onbeam/utils';
import { Game, UserRole } from '@prisma/client';
import { PlusIcon } from '@sphere/icons';
import { ButtonLink, MinimalSelect } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { useState } from 'react';

enum SortingOptions {
  CreatedAt = 'created-at',
  UpdatedAt = 'updated-at',
}

const sortMethodMap = {
  [SortingOptions.CreatedAt]: (games?: Game[]) =>
    games?.sort(
      (a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime(),
    ),
  [SortingOptions.UpdatedAt]: (games?: Game[]) =>
    games?.sort(
      (a, b) => new Date(b.updatedAt || '').getTime() - new Date(a.updatedAt || '').getTime(),
    ),
};

type Props = {
  address: string;
  games?: Game[];
};

export const AccountGames = ({ address, games }: Props) => {
  const { t } = useTranslation('account');
  const { user } = useAuthentication();
  const isOwner = compareAddresses(user?.address, address);
  const [sorting, setSorting] = useState<SortingOptions>(SortingOptions.CreatedAt);

  const sortedGames = sortMethodMap[sorting](games);

  return (
    <Container variant="breakout">
      <Flex py="space.24" justify="space-between">
        {isOwner && (user?.role === UserRole.CREATOR || user?.role === UserRole.ADMIN) && (
          <ButtonLink
            href={routes.external.gameDocs}
            target="_blank"
            leftIcon={<PlusIcon boxSize={rem(16)} />}
          >
            {t('games.cta-label')}
          </ButtonLink>
        )}
        <MinimalSelect
          value={sorting}
          onChange={value => setSorting(value as SortingOptions)}
          options={[
            {
              label: t('search:sort.options.created'),
              value: SortingOptions.CreatedAt,
            },
            {
              label: t('search:sort.options.updated'),
              value: SortingOptions.UpdatedAt,
            },
          ]}
          displayMobile
        />
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 4 }} spacing="space.24" pt="space.8">
        {sortedGames?.map(game => {
          const chain = getSupportedChainById(game.chainId);

          if (!chain) return null;

          return (
            <CollectionCard
              key={game.id}
              image={{
                src: game.logo?.src,
                alt: game.name,
              }}
              link={{
                href: routes.games.detail(game.slug, chain.routePrefix),
              }}
              creator={{
                avatar: game.avatar?.src,
                address,
              }}
              title={game.name}
              description={game.description}
            >
              {((isOwner && user?.role === UserRole.CREATOR) || user?.role === UserRole.ADMIN) && (
                <ButtonLink
                  w="full"
                  href={routes.account.games.index(game.slug, chain.routePrefix)}
                  variant="secondary"
                  mt="space.8"
                >
                  {t('games.card.cta.edit-label')}
                </ButtonLink>
              )}
            </CollectionCard>
          );
        })}
      </SimpleGrid>
      {!sortedGames?.length && (
        <CollectionsEmpty
          label={isOwner ? t('games.empty.own-account') : t('games.empty.other-account')}
        />
      )}
    </Container>
  );
};
